import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faPenToSquare, 
  faTrash,
  faSave,
  faTimes,
  faUser,
  faEnvelope,
  faKey,
  faCamera
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './Users.css';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await apiService.getUsers();
      setUsers(response.data.data || response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    }
  };

  const handleNewUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: ''
    });
    setAvatarFile(null);
    setAvatarPreview(null);
    setError('');
    setShowEditor(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: ''
    });
    setAvatarFile(null);
    setAvatarPreview((user.avatar && user.avatar.trim() !== '') ? `https://blog.bonlineco.com/storage/${user.avatar}` : null);
    setError('');
    setShowEditor(true);
  };

  const handleDelete = async (userId) => {
    if (currentUser?.id === userId) {
      alert('You cannot delete your own account!');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user? This will also delete all their posts.')) {
      try {
        await apiService.deleteUser(userId);
        setUsers(users.filter(u => u.id !== userId));
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let savedUser;
      
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        const response = await apiService.updateUser(editingUser.id, updateData);
        savedUser = response.data.user || response.data;
      } else {
        const response = await apiService.createUser(formData);
        savedUser = response.data.user || response.data;
      }

      // Upload avatar if one was selected
      if (avatarFile && savedUser && savedUser.id) {
        const avatarFormData = new FormData();
        avatarFormData.append('avatar', avatarFile);
        avatarFormData.append('user_id', savedUser.id);
        
        try {
          await apiService.uploadUserAvatar(savedUser.id, avatarFormData);
        } catch (avatarError) {
          console.error('Error uploading avatar:', avatarError);
        }
      }
      
      loadUsers();
      setShowEditor(false);
    } catch (error) {
      console.error('Error saving user:', error);
      setError(error.response?.data?.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  if (showEditor) {
    return (
      <div className="users-editor">
        <div className="users-editor-header">
          <h2>
            <FontAwesomeIcon icon={faUser} style={{ marginRight: '12px', color: '#10b981' }} />
            {editingUser ? 'Edit User' : 'Create New User'}
          </h2>
          <button 
            type="button" 
            onClick={() => setShowEditor(false)}
            className="users-btn users-btn-secondary"
          >
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
            Cancel
          </button>
        </div>

        <form onSubmit={handleSubmit} className="users-editor-form">
          {/* Avatar Section */}
          <div className="users-form-section">
            <h3 className="users-form-section-title">
              <FontAwesomeIcon icon={faCamera} />
              Profile Picture
            </h3>
            <div className="users-avatar-group">
            <div className="users-avatar-upload">
              <div className="users-avatar-preview">
                {(avatarPreview && avatarPreview.trim() !== '') ? (
                  <img 
                    src={avatarPreview} 
                    alt="Avatar" 
                    onError={(e) => { 
                      e.target.style.display = 'none';
                      setAvatarPreview(null);
                    }} 
                  />
                ) : (
                  <div className="users-avatar-placeholder">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                )}
                <label htmlFor="avatar-input" className="users-avatar-button">
                  <FontAwesomeIcon icon={faCamera} />
                  <input
                    type="file"
                    id="avatar-input"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              <div className="users-avatar-info">
                <h4>
                  <FontAwesomeIcon icon={faCamera} style={{ color: '#10b981' }} />
                  Profile Picture
                </h4>
                <p>Click the camera icon to upload a profile picture. A clear photo helps identify the user.</p>
                <div className="users-avatar-specs">
                  <span className="users-avatar-spec">
                    <FontAwesomeIcon icon={faUser} />
                    Square recommended
                  </span>
                  <span className="users-avatar-spec">
                    <FontAwesomeIcon icon={faCamera} />
                    Max 2MB
                  </span>
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Account Information Section */}
          <div className="users-form-section">
            <h3 className="users-form-section-title">
              <FontAwesomeIcon icon={faUser} />
              Account Information
            </h3>
            <div className="users-form-grid">
              <div className="users-form-group">
                <label htmlFor="name">
                  <FontAwesomeIcon icon={faUser} />
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="users-form-group">
                <label htmlFor="email">
                  <FontAwesomeIcon icon={faEnvelope} />
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div className="users-form-group users-form-group-full">
                <label htmlFor="password">
                  <FontAwesomeIcon icon={faKey} />
                  Password {editingUser ? '(Optional)' : '*'}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Min 8 characters'}
                  required={!editingUser}
                  minLength="8"
                    />
                <small className="users-form-hint">
                  <FontAwesomeIcon icon={faKey} />
                  {editingUser ? 'Only fill this if you want to change the password' : 'Minimum 8 characters required'}
                </small>
              </div>
            </div>
          </div>

          {error && (
            <div className="users-error">{error}</div>
          )}

          <div className="users-form-actions">
            <button 
              type="submit" 
              className="users-btn users-btn-primary"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} />
              {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
            </button>
            <button 
              type="button" 
              onClick={() => setShowEditor(false)}
              className="users-btn users-btn-secondary"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="users-header">
        <div>
          <h2>Users Management</h2>
          <p>Manage user accounts and permissions</p>
        </div>
        <button onClick={handleNewUser} className="users-btn users-btn-primary">
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
          New User
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Posts</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="users-empty">
                  <FontAwesomeIcon icon={faUser} style={{ fontSize: '32px', color: '#cbd5e1', marginBottom: '12px' }} />
                  <p>No users found</p>
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="users-user-cell">
                      <div className="users-avatar">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="users-user-info">
                        <span className="users-user-name">{user.name}</span>
                        {currentUser?.id === user.id && (
                          <span className="users-badge">You</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className="users-count-badge">
                      {user.posts_count || 0}
                    </span>
                  </td>
                  <td className="users-date">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="users-actions">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="users-action-btn users-action-edit"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="users-action-btn users-action-delete"
                        title="Delete"
                        disabled={currentUser?.id === user.id}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
